import React, { useState } from 'react';
import { Building2, Eye, EyeOff, KeyRound, UserRound } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { AssociateData, changeDipendentePassword } from '../services/otpAuthService';
import { nomeDipendente } from '../utils/sezioniDipendente';

/** Motivi del 400 sulla password nuova (stessa policy della password dello studio). */
const MOTIVI_PASSWORD: Record<string, string> = {
  too_short: 'La password deve avere almeno 8 caratteri',
  too_obvious: 'Password troppo prevedibile: evita il tuo nome, quello dello studio o la Partita IVA',
  common: 'Password troppo comune: scegline una meno facile da indovinare',
  uguale_titolare: 'La password del dipendente deve essere diversa da quella dello studio',
};

/**
 * Scheda «Il mio accesso» del dipendente dello studio (spec dipendenti §5.4, §6.2): chi è, di
 * quale studio, e il cambio della propria password. Il profilo dello studio resta del titolare.
 */
const IlMioAccessoDipendente: React.FC<{ associate: AssociateData }> = ({ associate }) => {
  const toast = useToast();
  const [aperto, setAperto] = useState(false);
  const [attuale, setAttuale] = useState('');
  const [nuova, setNuova] = useState('');
  const [conferma, setConferma] = useState('');
  const [mostra, setMostra] = useState(false);
  const [salvataggio, setSalvataggio] = useState(false);

  const azzera = () => { setAttuale(''); setNuova(''); setConferma(''); setAperto(false); };

  const salva = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!attuale) { toast.error('Inserisci la password attuale'); return; }
    if (nuova.length < 8) { toast.error('La password deve avere almeno 8 caratteri'); return; }
    if (nuova !== conferma) { toast.error('Le due password non coincidono'); return; }
    setSalvataggio(true);
    try {
      const res = await changeDipendentePassword(attuale, nuova);
      if (res.success) {
        toast.success('Password aggiornata');
        azzera();
      } else {
        toast.error(
          (res.reason === 'uguale_titolare'
            ? MOTIVI_PASSWORD.uguale_titolare
            : res.message || (res.reason && MOTIVI_PASSWORD[res.reason]))
          || 'Impossibile salvare la password'
        );
      }
    } catch (err: any) {
      toast.error(err?.message || 'Errore durante il salvataggio della password');
    } finally {
      setSalvataggio(false);
    }
  };

  const inputClass = 'w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none';

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      {/* Chi sono */}
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-lg border border-gray-200 dark:border-gray-800 p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-md shadow-blue-500/20">
            <UserRound className="w-5 h-5 text-white" />
          </div>
          <h2 className="text-base font-bold text-gray-900 dark:text-white">Il mio accesso</h2>
        </div>
        <dl className="space-y-4">
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1 flex items-center gap-1">
              <UserRound className="w-3 h-3" /> Nome e cognome
            </dt>
            <dd className="text-sm font-semibold text-gray-900 dark:text-white">{nomeDipendente(associate) || '—'}</dd>
          </div>
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1 flex items-center gap-1">
              <Building2 className="w-3 h-3" /> Studio
            </dt>
            <dd className="text-sm font-semibold text-gray-900 dark:text-white">{associate.nominativo}</dd>
            {associate.citta && (
              <dd className="text-xs text-gray-500 dark:text-gray-400">{associate.citta}{associate.provincia ? ` (${associate.provincia})` : ''}</dd>
            )}
          </div>
        </dl>
        <p className="mt-5 text-xs text-gray-500 dark:text-gray-400">
          Sei entrato come dipendente dello studio: i servizi che vedi li sceglie il titolare.
        </p>
      </div>

      {/* Password */}
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-lg border border-gray-200 dark:border-gray-800 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg flex items-center justify-center shadow-md shadow-indigo-500/20">
            <KeyRound className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <h2 className="text-base font-bold text-gray-900 dark:text-white">Password</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">Per cambiarla serve quella attuale. Se l'hai dimenticata, chiedi al titolare dello studio.</p>
          </div>
          {!aperto && (
            <button type="button" onClick={() => setAperto(true)} className="px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700">
              Cambia
            </button>
          )}
        </div>

        {aperto && (
          <form onSubmit={salva} className="space-y-3">
            <input type={mostra ? 'text' : 'password'} value={attuale} onChange={(e) => setAttuale(e.target.value)} placeholder="Password attuale" autoComplete="current-password" aria-label="Password attuale" className={inputClass} />
            <input type={mostra ? 'text' : 'password'} value={nuova} onChange={(e) => setNuova(e.target.value)} placeholder="Nuova password (almeno 8 caratteri)" autoComplete="new-password" aria-label="Nuova password" className={inputClass} />
            <input type={mostra ? 'text' : 'password'} value={conferma} onChange={(e) => setConferma(e.target.value)} placeholder="Ripeti la nuova password" autoComplete="new-password" aria-label="Ripeti la nuova password" className={inputClass} />
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <button type="button" onClick={() => setMostra((v) => !v)} className="text-xs text-gray-500 dark:text-gray-400 inline-flex items-center gap-1">
                {mostra ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />} {mostra ? 'Nascondi' : 'Mostra'}
              </button>
              <div className="flex gap-2">
                <button type="button" onClick={azzera} className="px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700">Annulla</button>
                <button type="submit" disabled={salvataggio} className="px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                  {salvataggio ? 'Salvataggio…' : 'Salva password'}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default IlMioAccessoDipendente;
